import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, AlertTriangle, Info } from "lucide-react";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { formatDateTime } from "@/lib/date";

const LEVEL_META = {
  info: { icon: Info, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  warning: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  error: { icon: AlertCircle, cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function AutomationLogsTab({ sources }) {
  const [level, setLevel] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (level) params.level = level;
    api.adminAutoListLogs(params)
      .then((d) => setItems(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 200 };
    if (level) params.level = level;
    api.adminAutoListLogs(params)
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [level]);

  const nameFor = (sid) => sources.find((s) => s.id === sid)?.name || "—";

  return (
    <section>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-blueblack">Registros de errores</h2>
          <p className="text-xs text-textgray">Cada ejecución escribe eventos INFO/WARNING/ERROR que quedan disponibles para diagnóstico.</p>
        </div>
        <div className="ml-auto">
          <div className="text-[10px] uppercase tracking-widest text-textgray mb-1">Nivel</div>
          <Select value={level || "all"} onValueChange={(v) => setLevel(v === "all" ? "" : v)}>
            <SelectTrigger className="min-w-[180px]"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-softblue divide-y divide-softblue/60">
        {loading && (
          <div className="p-8 text-center text-textgray">
            <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-textgray">
            No hay registros aún. Se poblará automáticamente.
          </div>
        )}
        {items.map((log) => {
          const meta = LEVEL_META[log.level] || LEVEL_META.info;
          const Ic = meta.icon;
          return (
            <div key={log.id} className="p-4 flex gap-3">
              <Ic className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={meta.cls}>{log.level}</Badge>
                  <span className="text-[11px] text-textgray">{formatDateTime(log.created_at)}</span>
                  {log.source_id && <span className="text-[11px] text-textgray">· {nameFor(log.source_id)}</span>}
                </div>
                <p className="text-sm text-blueblack mt-1">{log.message}</p>
                {log.details && Object.keys(log.details).length > 0 && (
                  <pre className="mt-1 text-[11px] font-mono text-textgray bg-lightgray/60 p-2 overflow-x-auto">
                    {JSON.stringify(log.details, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
